/**
 * Sprint 22 — persistence helpers for RAID-item analysis modals
 * (Fishbone, 5 Whys, Pareto, FMEA, Monte Carlo, Bow Tie).
 *
 * Each modal owns an opaque state blob. We store it under
 * `raid_item.extra_metadata.analyses[<type>]` so:
 *
 *   - No new table / migration needed (the column already accepts JSON).
 *   - Different analysis types coexist on the same risk/issue.
 *   - Re-opening a modal pre-loads the user's last input.
 *
 * The blob shape is the modal's responsibility — this layer never
 * inspects it. We DO preserve other extra_metadata keys (category,
 * priority, etc. that mapRaidToIssue / mapRaidToRisk read) so the
 * save round-trip never silently drops adjacent fields.
 */
import { projectRaidService } from '../../services/projectRaid'

export type AnalysisKey =
  | 'fishbone'
  | '5whys'
  | 'pareto'
  | 'fmea'
  | 'montecarlo'
  | 'bowtie'

/**
 * Pull just the analysis blob for a given key from a raid item.
 * Returns null when the item has no saved analysis of that type yet.
 */
export async function loadAnalysis<T = unknown>(
  projectId: number,
  raidApiId: number,
  key: AnalysisKey,
): Promise<T | null> {
  const item = await projectRaidService.get(projectId, raidApiId)
  const meta = (item.extra_metadata || {}) as Record<string, unknown>
  const analyses = (meta.analyses || {}) as Record<string, unknown>
  return (analyses[key] as T | undefined) ?? null
}

/**
 * Merge a new analysis blob into extra_metadata.analyses[key] and PATCH.
 *
 * We re-fetch the item first so concurrent edits to OTHER analysis keys
 * (or to category/priority/etc.) don't get clobbered by this write.
 * Last-write-wins on the same key is intentional — these are interactive
 * analyses, not collaborative documents.
 */
export async function saveAnalysis(
  projectId: number,
  raidApiId: number,
  key: AnalysisKey,
  state: unknown,
): Promise<void> {
  const item = await projectRaidService.get(projectId, raidApiId)
  const meta = { ...((item.extra_metadata || {}) as Record<string, unknown>) }
  const analyses = { ...((meta.analyses || {}) as Record<string, unknown>) }
  analyses[key] = {
    state,
    saved_at: new Date().toISOString(),
  }
  meta.analyses = analyses
  await projectRaidService.update(projectId, raidApiId, { extra_metadata: meta })
}
